import React, { useRef, forwardRef, useCallback, useState } from "react";
import classNames from "classnames";
import PropTypes from "prop-types";

import DropDown from "../DropDown/DropDown";
import SelectBodyTypeRadioButton from "./SelectBodyTypeRadioButton";
import Typography from "../Typography/Typography";
import Input from "../Input/Input";
import ArrowIcon from "../ArrowIcon/ArrowIcon";

import { getOptionKey } from "../../utils/select";
import { handleReactRef } from "../../utils/react";

import styles from "./SelectVariantInput.module.scss";
import { handleKeyDownNavigation } from "../../utils/keyboard-navigation/keyboard-navigation";

const SelectVariantInput = forwardRef(({
  isParseValueToPrimitiveOnChange,
  withArrow = false,
  options,
  value,
  disabled,
  onChange,
  parentNode,
  label,
  onHide,
  searchValue,
  isWidthBasedOnInput = true,
  isJustifyEnd = false,
  popupProps,
  inputProps,
}, ref) => {
  const [instanceRoot, setInstanceRoot] = useState(null);
  const refRadioGroup = useRef(null);

  const handleChange = (value, index) => {
    if (instanceRoot) {
      instanceRoot.hide();
    }

    if (value) {
      onChange(isParseValueToPrimitiveOnChange ? getOptionKey(value) : value, index);
    }
  };

  const handleInputKeyDown = (e) => {
    handleKeyDownNavigation(refRadioGroup.current, e, false, false);
  };

  const key = getOptionKey(value);
  const labelOptionActive = options?.find((o) => getOptionKey(o) === key)?.label || value?.label;

  const handleRef = useCallback((el) => {
    handleReactRef(ref, el);

    setInstanceRoot(el);
  }, [ref]);

  const isInputReadOnly = !inputProps || !("readOnly" in inputProps) || inputProps.readOnly;

  return (
    <DropDown
      ref={handleRef}
      noPortal
      isMinWidthBasedOnButton
      isMaxWidthBasedOnButton={isWidthBasedOnInput}
      toggleOnClickButton={isInputReadOnly}
      focusOnBecameVisible={isInputReadOnly}
      className={classNames(styles["root"], isJustifyEnd ? styles["justify-end"] : "")}
      disabled={disabled}
      boundaryRect={parentNode?.getBoundingClientRect()}
      popupProps={{
        contentProps: {
          className: styles["popup-content"],
        },
        hideOnScroll: false,
        usePositionAbsolute: true,
        ...popupProps,
      }}
      onHide={onHide}
      button={({
        show,
        onClick
      }) => {
        const slotAfterInput = withArrow
          ? <ArrowIcon direction={show ? ArrowIcon.direction.DOWN : ArrowIcon.direction.RIGHT}/>
          : undefined;

        return (
          <>
            {label && (
              <Typography
                variant={Typography.variant.body2}
                color={disabled ? Typography.color.iconDisabled : undefined}
              >{label}</Typography>
            )}
            <Input
              disabled={disabled}
              active={show}
              value={labelOptionActive}
              onChange={() => {}} // temporary, to prevent React error in console.
              className={styles["input"]}
              onClick={onClick}
              onKeyDown={handleInputKeyDown}
              slotAfter={slotAfterInput}
              {...inputProps}
              readOnly={isInputReadOnly}
            />
          </>
        );
      }}
      body={({ hide }) => (
        <SelectBodyTypeRadioButton
          ref={refRadioGroup}
          value={value}
          searchValue={searchValue}
          options={options}
          onSelect={(item, indexOption, indexGroup, e) => {
            handleChange(item, indexOption, indexGroup);
            hide(e);
          }}
        />
      )}
    />
  );
});

SelectVariantInput.propTypes = {
  options: PropTypes.array,
  value: PropTypes.oneOfType([PropTypes.object, PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func,
  parentNode: PropTypes.any,
  label: PropTypes.string,
  disabled: PropTypes.bool,
  withArrow: PropTypes.bool,
  isWidthBasedOnInput: PropTypes.bool,
};

export default SelectVariantInput;
